import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  memo
} from 'react';
import {
  Container,
  makeStyles,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Typography,
  TextField,
  Menu,
  MenuItem,
  Tooltip,
  Button,
  CircularProgress,
  Box
} from '@material-ui/core';
import {
  Send,
  X,
  RefreshCw,
  FileText,
  DownloadCloud,
  AlertCircle,
  PlayCircle,
  Mic,
  Smile,
  MapPin,
  User as UserIcon,
  CheckCircle,
  Copy,
  Check
} from 'react-feather';
import uuid from 'react-uuid';

import Page from 'src/shared/ui/components/Page';
import Datatable from 'src/shared/ui/components/Datatable';

import getConversationList from 'src/conversation/use-cases/getConversationList';
import getConversationListFromName from 'src/conversation/use-cases/getConversationListFromName';
import getConversationMessageMediaById from 'src/conversation/use-cases/getConversationMessageMediaById';
import sendMessage from 'src/conversation/use-cases/sendMessage';
import { capitalize } from 'src/shared/support/string';

const getGreetingLocal = () => {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 12) return 'Buenos días';
  if (hour >= 12 && hour < 20) return 'Buenas tardes';
  return 'Buenas noches';
};

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  dialogPaper: {
    height: '100vh',
    borderRadius: 0
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#4caf50',
    color: 'white',
    padding: theme.spacing(1)
  },
  messagesContainer: {
    height: 'calc(100vh - 140px)',
    overflowY: 'auto',
    backgroundColor: '#E5DDD5',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  message: {
    display: 'flex',
    maxWidth: '70%'
  },
  ownMessage: {
    alignSelf: 'flex-end'
  },
  remoteMessage: {
    alignSelf: 'flex-start'
  },
  systemMessage: {
    alignSelf: 'center',
    maxWidth: '100%'
  },
  bubble: {
    padding: theme.spacing(1.5),
    borderRadius: 18,
    maxWidth: '100%'
  },
  timestamp: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    opacity: 0.7,
    fontSize: '0.7rem'
  },
  inputContainer: {
    padding: theme.spacing(2),
    backgroundColor: '#F0F0F0',
    borderTop: '1px solid #DDD',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 25
    }
  },
  refreshButton: {
    float: 'right'
  }
}));

const MediaMessage = ({
  messageId,
  type,
  caption,
  onMediaLoaded,
  onStatusChange
}) => {
  const [src, setSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const lowerCaption = (caption || '').toLowerCase();
  const isPdfByName = lowerCaption.endsWith('.pdf');

  useEffect(() => {
    return () => {
      if (src) URL.revokeObjectURL(src);
    };
  }, [src]);

  const handleLoadMedia = async () => {
    // Para IMAGE/VIDEO/AUDIO/VOICE/STICKER, si ya está cargado, no repetir
    if (src && type !== 'DOCUMENT') return;

    setIsLoading(true);
    setError(false);
    onStatusChange?.('START');

    try {
      const blob = await getConversationMessageMediaById({ id: messageId });
      const objectUrl = URL.createObjectURL(blob);

      if (type === 'DOCUMENT') {
        // Descargar y listo (no dejamos vista previa)
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = caption || (isPdfByName ? 'documento.pdf' : 'documento'); // sugiere nombre [web:56]
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();

        // No revocar inmediatamente para no romper la descarga en algunos navegadores [web:71]
        setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000); // libera recursos [web:67]
        if (onMediaLoaded) onMediaLoaded();
        return;
      }

      // Media normal (se renderiza embebido)
      setSrc(objectUrl);
      if (onMediaLoaded) onMediaLoaded();
    } catch (err) {
      console.error('Error cargando media/documento', err);
      setError(true);
    } finally {
      setIsLoading(false);
      onStatusChange?.('END');
    }
  };

  const contentStyle = {
    maxWidth: '100%',
    borderRadius: '8px',
    marginBottom: caption ? '8px' : '0',
    display: 'block'
  };

  const renderContent = () => {
    if (!src) return null;
    if (type === 'IMAGE' || type === 'STICKER')
      return <img src={src} alt="Media" style={contentStyle} />;
    if (type === 'VIDEO')
      return <video src={src} controls style={contentStyle} />;
    if (type === 'AUDIO' || type === 'VOICE')
      return (
        <audio src={src} controls style={{ ...contentStyle, width: '250px' }} />
      );
    return null;
  };

  // Si es media embebida ya cargada (IMAGE/VIDEO/AUDIO/VOICE/STICKER)
  if (src) {
    return (
      <div style={{ minWidth: '100px', minHeight: '50px' }}>
        {renderContent()}
        {caption && <Typography variant="body2">{caption}</Typography>}
      </div>
    );
  }

  // Placeholder (incluye DOCUMENT)
  const buttonLabel =
    type === 'IMAGE'
      ? 'Cargar Foto'
      : type === 'STICKER'
      ? 'Cargar Sticker'
      : type === 'VIDEO'
      ? 'Cargar Video'
      : type === 'AUDIO'
      ? 'Cargar Audio'
      : type === 'VOICE'
      ? 'Cargar Nota de Voz'
      : type === 'DOCUMENT'
      ? isPdfByName
        ? 'Descargar PDF'
        : 'Descargar archivo'
      : 'Cargar';

  const icon =
    type === 'VIDEO' ? (
      <PlayCircle size={24} color="#666" style={{ marginBottom: 8 }} />
    ) : type === 'DOCUMENT' ? (
      <DownloadCloud size={24} color="#666" style={{ marginBottom: 8 }} />
    ) : type === 'VOICE' ? (
      <Mic size={24} color="#666" style={{ marginBottom: 8 }} />
    ) : type === 'STICKER' ? (
      <Smile size={24} color="#666" style={{ marginBottom: 8 }} />
    ) : (
      <DownloadCloud size={24} color="#666" style={{ marginBottom: 8 }} />
    );

  return (
    <div style={{ minWidth: '150px' }}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        bgcolor="#f0f2f5"
        borderRadius={2}
        p={2}
        style={{
          width: type === 'AUDIO' || type === 'VOICE' ? '250px' : '200px',
          height: type === 'AUDIO' || type === 'VOICE' ? '80px' : '150px',
          border: '1px dashed #ccc'
        }}
      >
        {isLoading ? (
          <>
            <CircularProgress size={24} style={{ marginBottom: 8 }} />
            <Typography variant="caption" color="textSecondary">
              {type === 'DOCUMENT'
                ? 'Preparando descarga...'
                : 'Descargando...'}
            </Typography>
          </>
        ) : error ? (
          <>
            <AlertCircle
              size={24}
              color="#f44336"
              style={{ marginBottom: 8 }}
            />
            <Typography
              variant="caption"
              color="error"
              style={{ marginBottom: 8 }}
            >
              Error
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={handleLoadMedia}
              startIcon={<RefreshCw size={14} />}
            >
              Reintentar
            </Button>
          </>
        ) : (
          <>
            {icon}
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleLoadMedia}
              style={{ textTransform: 'none', borderRadius: 20 }}
            >
              {buttonLabel}
            </Button>
          </>
        )}
      </Box>

      {/* Para DOCUMENT quieres que el texto sea el nombre del archivo: ya viene en caption (msg.text) */}
      {caption && (
        <Typography variant="body2" style={{ marginTop: 4 }}>
          {caption}
        </Typography>
      )}
    </div>
  );
};

const LocationMessage = ({ msg }) => {
  const { latitude, longitude, name, address } = msg.metadata || {};
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

  return (
    <Box
      p={1}
      bgcolor="#f0f2f5"
      borderRadius={8}
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <MapPin size={32} color="#4caf50" style={{ marginBottom: 8 }} />
      {name && (
        <Typography variant="subtitle2" align="center">
          {name}
        </Typography>
      )}
      {address && (
        <Typography variant="caption" align="center" color="textSecondary">
          {address}
        </Typography>
      )}
      <Button
        variant="outlined"
        color="primary"
        size="small"
        href={googleMapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ marginTop: 8, textTransform: 'none' }}
      >
        Ver en Google Maps
      </Button>
    </Box>
  );
};

const ContactCard = ({ name, phone, rawText }) => {
  const [copied, setCopied] = useState(false);

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Fallback copy failed', err);
    }
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    const textToCopy = phone || rawText || name;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => fallbackCopy(textToCopy));
    } else {
      fallbackCopy(textToCopy);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      style={{ userSelect: 'text' }}
    >
      <Box
        display="flex"
        alignItems="center"
        style={{ minWidth: 0, marginRight: 8 }}
      >
        <UserIcon
          size={24}
          color="#666"
          style={{ marginRight: 8, flexShrink: 0 }}
        />
        <Box style={{ minWidth: 0, userSelect: 'text' }}>
          <Typography
            variant="body2"
            style={{
              fontWeight: 'bold',
              userSelect: 'text',
              wordBreak: 'break-word'
            }}
          >
            {name}
          </Typography>
          {phone ? (
            <Typography
              variant="caption"
              display="block"
              color="textSecondary"
              style={{ userSelect: 'text' }}
            >
              {phone}
            </Typography>
          ) : rawText ? (
            <Typography
              variant="caption"
              display="block"
              color="textSecondary"
              style={{ userSelect: 'text' }}
            >
              {rawText}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Tooltip title={copied ? '¡Copiado!' : 'Copiar'}>
        <IconButton
          size="small"
          onClick={handleCopy}
          style={{ padding: 4, flexShrink: 0 }}
        >
          {copied ? (
            <Check size={16} color="#4caf50" />
          ) : (
            <Copy size={16} color="#666" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const ContactMessage = ({ msg }) => {
  const contacts = msg.metadata?.contacts || [];

  if (contacts.length === 0) {
    const text = msg.text || '';
    const parts = text.includes(':') ? text.split(':') : [text];
    const name = parts[0]?.trim() || 'Contacto';
    const phone = parts
      .slice(1)
      .join(':')
      .trim();

    return (
      <Box
        p={1}
        bgcolor="#f0f2f5"
        borderRadius={8}
        minWidth="200px"
        style={{ userSelect: 'text' }}
      >
        <ContactCard name={name} phone={phone} rawText={text} />
      </Box>
    );
  }

  const trimmedText = (msg.text || '').trim();
  const isAutoGeneratedText = contacts.some((c) => {
    const name =
      (typeof c.name === 'string'
        ? c.name
        : c.name?.formattedName ||
          c.name?.formatted_name ||
          c.name?.firstName) ||
      c.formattedName ||
      '';
    const phones = Array.isArray(c.phones)
      ? c.phones
          .map((p) => (typeof p === 'string' ? p : p.phone || p.wa_id))
          .filter(Boolean)
      : [c.phoneNumber || c.phone].filter(Boolean);
    const phone = phones[0] || '';
    const autoGen1 = phone ? `${name}: ${phone}` : name;
    const autoGen2 = phone ? `${name}\n${phone}` : name;
    return trimmedText === autoGen1 || trimmedText === autoGen2;
  });

  const accompanyingText =
    !isAutoGeneratedText && trimmedText ? trimmedText : null;

  return (
    <Box
      p={1}
      bgcolor="#f0f2f5"
      borderRadius={8}
      minWidth="200px"
      style={{ userSelect: 'text' }}
    >
      {contacts.map((contact, idx) => {
        const contactName =
          (typeof contact.name === 'string'
            ? contact.name
            : contact.name?.formattedName ||
              contact.name?.formatted_name ||
              contact.name?.firstName) ||
          contact.formattedName ||
          'Contacto';

        const phones = Array.isArray(contact.phones)
          ? contact.phones
              .map((p) => (typeof p === 'string' ? p : p.phone || p.wa_id))
              .filter(Boolean)
          : [contact.phoneNumber || contact.phone].filter(Boolean);

        const phoneDisplay = phones.join(', ');

        return (
          <Box key={idx} mb={idx < contacts.length - 1 ? 1 : 0}>
            <ContactCard
              name={contactName}
              phone={phoneDisplay}
              rawText={msg.text}
            />
          </Box>
        );
      })}
      {accompanyingText && (
        <Typography
          variant="body2"
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: '1px solid #e0e0e0',
            whiteSpace: 'pre-wrap',
            userSelect: 'text'
          }}
        >
          {accompanyingText}
        </Typography>
      )}
    </Box>
  );
};

const MessageBubble = memo(
  ({ msg, classes, onMediaLoaded, onStatusChange }) => {
    const isMedia = [
      'IMAGE',
      'VIDEO',
      'AUDIO',
      'DOCUMENT',
      'VOICE',
      'STICKER'
    ].includes(msg.type);
    return (
      <div
        className={`${classes.message} ${
          msg.type === 'CLOSED'
            ? classes.systemMessage
            : msg.origin === 'OUTBOUND'
            ? classes.ownMessage
            : classes.remoteMessage
        }`}
      >
        <Paper
          elevation={msg.type === 'CLOSED' ? 0 : 1}
          className={classes.bubble}
          style={
            msg.type === 'CLOSED'
              ? {
                  backgroundColor: '#f0f2f5',
                  color: '#666',
                  fontSize: '0.75rem',
                  padding: '4px 12px'
                }
              : {}
          }
        >
          {msg.metadata?.fullName ? (
            <Typography
              variant="body2"
              style={{
                fontWeight: 'bold',
                marginBottom: 4,
                fontSize: '0.8rem',
                color: '#555',
                whiteSpace: 'pre-wrap'
              }}
            >
              {msg.metadata.fullName}
            </Typography>
          ) : (
            ''
          )}
          {isMedia ? (
            <MediaMessage
              messageId={msg.id}
              type={msg.type}
              caption={msg.text}
              onMediaLoaded={onMediaLoaded}
              onStatusChange={onStatusChange}
            />
          ) : msg.type === 'LOCATION' ? (
            <LocationMessage msg={msg} />
          ) : msg.type === 'CONTACT' ? (
            <ContactMessage msg={msg} />
          ) : (
            <Typography
              variant="body2"
              style={{
                whiteSpace: 'pre-wrap',
                fontWeight: msg.type === 'CLOSED' ? 'bold' : 'normal'
              }}
            >
              {msg.text}
            </Typography>
          )}
          <Typography variant="caption" className={classes.timestamp}>
            {new Date(msg.updatedAt).toLocaleString()}
          </Typography>
        </Paper>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.msg.id === nextProps.msg.id &&
      prevProps.msg.updatedAt === nextProps.msg.updatedAt
    );
  }
);

const MessagesList = memo(
  ({
    rows,
    classes,
    onScroll,
    scrollRef,
    messagesEndRef,
    onMediaLoaded,
    onStatusChange
  }) => {
    useLayoutEffect(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, [rows.length]);

    return (
      <DialogContent
        className={classes.messagesContainer}
        dividers
        ref={scrollRef}
        onScroll={onScroll}
      >
        {rows.map((msg) => (
          <MessageBubble
            key={msg.id || uuid()}
            msg={msg}
            classes={classes}
            onMediaLoaded={onMediaLoaded}
            onStatusChange={onStatusChange}
          />
        ))}
        <div ref={messagesEndRef} />
      </DialogContent>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.rows.length !== nextProps.rows.length) return false;
    if (prevProps.rows.length === 0) return true;
    const lastPrev = prevProps.rows[prevProps.rows.length - 1];
    const lastNext = nextProps.rows[nextProps.rows.length - 1];
    return lastPrev.id === lastNext.id;
  }
);

const MessageInput = ({ onSendMessage, templates, user }) => {
  const classes = useStyles();
  const [text, setText] = useState('');
  const [anchorElTemplates, setAnchorElTemplates] = useState(null);
  const [isSending, setIsSending] = useState(false); // <--- Nuevo estado de bloqueo

  const handleSend = async () => {
    // Bloqueamos si está vacío o ya se está enviando
    if (!text.trim() || isSending) return;

    setIsSending(true);
    try {
      await onSendMessage(text); // Esperamos a que el padre termine
      setText('');
    } catch (error) {
      console.error('Error al enviar desde input', error);
    } finally {
      setIsSending(false); // Desbloqueamos
    }
  };

  const handleTemplateSelect = (templateText) => {
    const finalText = templateText.replace(
      '{{getGreetingLocal}}',
      getGreetingLocal()
    );
    setText(finalText);
    setAnchorElTemplates(null);
  };

  return (
    <DialogActions className={classes.inputContainer}>
      <Tooltip title="Usar plantilla">
        <IconButton
          color="default"
          onClick={(e) => setAnchorElTemplates(e.currentTarget)}
          style={{ padding: 8 }}
          disabled={isSending} // Deshabilitar plantillas al enviar
        >
          <FileText size={20} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorElTemplates}
        keepMounted
        open={Boolean(anchorElTemplates)}
        onClose={() => setAnchorElTemplates(null)}
      >
        {templates.map((template, idx) => (
          <MenuItem
            key={idx}
            onClick={() => handleTemplateSelect(template.text)}
          >
            {template.label}
          </MenuItem>
        ))}
      </Menu>

      <TextField
        fullWidth
        multiline
        maxRows={4}
        variant="outlined"
        placeholder={isSending ? 'Enviando...' : 'Escribe un mensaje...'}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className={classes.inputField}
        disabled={isSending} // Deshabilitar input
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!isSending) handleSend(); // Bloqueo extra por seguridad
          }
        }}
      />
      <IconButton
        color="primary"
        onClick={handleSend}
        disabled={!text.trim() || isSending} // Botón deshabilitado
      >
        {isSending ? <CircularProgress size={20} /> : <Send size={20} />}
      </IconButton>
    </DialogActions>
  );
};

const ClosureDialog = ({ open, onClose, onConfirm, conversationToClose }) => {
  const [comments, setComments] = useState('');

  useEffect(() => {
    if (!open) setComments('');
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        setComments('');
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Cerrar Conversación</DialogTitle>
      <DialogContent>
        <DialogContentText>
          ¿Estás seguro de que deseas cerrar esta conversación? Esto quedará
          registrado como un mensaje interno de &quot;CONVERSACIÓN
          CERRADA&quot;.
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label="Comentarios opcionales"
          type="text"
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
            setComments('');
          }}
          color="primary"
        >
          Cancelar
        </Button>
        <Button
          onClick={() => onConfirm(comments)}
          color="primary"
          variant="contained"
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ConversationListView = () => {
  const user = JSON.parse(localStorage.user);

  const TEMPLATES = [
    {
      label: 'SALUDO NUEVO CLIENTE',
      text: `{{getGreetingLocal}}, le saluda ${capitalize(
        user.firstname
      )}, de la firma DomoSmart SA.`
    },
    {
      label: 'SALUDO CLIENTE',
      text: `{{getGreetingLocal}} estimado cliente,
¿Cómo está?
Le saluda ${capitalize(user.firstname)}`
    },
    {
      label: 'DERIVAMOS A COBRANZA',
      text:
        'Le agradezco, derivamos al área de cobranza para la verificacion de su pago'
    },
    {
      label: 'VERIFICAMOS PAGO',
      text:
        'Le agradezco, estaremos verificando y procesando en la brevedad posible'
    },
    {
      label: 'SALUDO INTERESADO',
      text: `{{getGreetingLocal}},
¿Cómo esta? le saluda ${capitalize(user.firstname)} de DomoSmart

Estaría interesado en adquirir el servicio de rastreo (GPS) para un vehículo o moto? así le facilito el detalle`
    },
    {
      label: 'PLANES NACIONALES AUTO',
      text: `Contamos con 4 planes según el nivel de instalación que prefiera, le facilito los Planes Nacionales de Rastreo Satelital para Auto:

100.000 gs mensual, instalación comercial bonificada(se instala lo más oculto posible en el tablero del vehículo, no cuenta con seguro del dispositivo ni de contrato)

100.000 gs mensual, con instalación Premium de 150.000 gs(fraccionable con la cuota) El dispositivo de rastreo se instala fuera del tablero del vehículo e incluye seguro del dispositivo y contrato.

115.000 gs mensual, con instalación Premium sin coste.El dispositivo de rastreo se instala fuera del tablero del vehículo e incluye seguro del dispositivo y contrato.

100.000 gs mensual, con instalación Premium más instalación Señuelo de 450.000 gs(fraccionable con la cuota).El dispositivo de rastreo se instala fuera del tablero del vehículo.Incluye un segundo equipo de rastreo adicional, seguro de dispositivo y contrato.`
    },
    {
      label: 'DETALLES SERVICIO AUTO DESDE APP',
      text: `Le paso los detalles del Servicio de GPS para Auto que puede controlar desde la APP: 📱 🖥️
- Historial del vehículo 🚗 🚚 🚜 y movimiento en tiempo real
- Notificaciones 📩
- Estado de la batería del vehículo 🪫 🔋
- Calificación de conducción 📊
- Puesta en marcha y detenciones
- Modo seguro(alarma que le notifica si el vehículo se mueve, golpea o enciende) 🔊
- Batería de respaldo(por si desconectan 🚫 la batería del vehículo)
- Parada de motor y activación desde la App 🛑 (según modelo de vehículo)

Atención telefónica 24 / 7 ✅ Ayudamos a recuperar su vehículo en caso de robo`
    },
    {
      label: 'DETALLES SERVICIO MOTO',
      text: `Le paso los detalles del Servicio de GPS para Moto:
- Historial de la moto 🏍️ 🛵 y movimiento en tiempo real 📈
- Notificaciones 📩
- Estado de la batería de la moto 🔋
- Calificación de conducción 📊
- Puesta en marcha y detenciones
- Modo seguro(alarma que le notifica si la moto se mueve, golpea o enciende) 🔊
- Batería de respaldo(por si desconectan 🚫 la batería de la moto)

Atención telefónica 24 / 7 ✅.Ayudamos a recuperar su vehículo en caso de robo.`
    },
    {
      label: 'PLANES NACIONALES MOTO',
      text: `Le facilito los Planes Nacionales de Rastreo Satelital para moto:

100.000 gs mensual instalación comercial bonificada. (No lleva soldadura en el chasis no cuenta con seguro del dispositivo y contrato)

100.000 gs mensual, con instalación Premium de 150.000 gs(fraccionable con la cuota).Incluye soldadura de una planchuela en el chasis, seguro de dispositivo y contrato.

100.000Gs mensual, con instalación Premium más Señuelo de 250.000 gs(fraccionable con la cuota).Incluye un segundo equipo de rastreo adicional, seguro de dispositivo y contrato.`
    },
    {
      label: 'DIRECCIÓN SEDE CENTRAL',
      text: `Domosmart Oficina Central

https://maps.google.com/maps/search/Domosmart/@-25.3701,-57.5822,17z?hl=es

Hungría Esquina Jamaica, Barrio Centro, Villa Elisa, Paraguay

Horarios habilitados 8 a 18 de lunes a viernes y 8 a 13 los sábados.

Servicios técnicos con previo agendamiento.`
    },
    {
      label: 'DIRECCIÓN SEDE CDE',
      text: `DomoSmart Sucursal CDE

https://maps.app.goo.gl/zyx61MamNJMqoJER7

Horarios habilitados 7 a 17 de lunes a viernes y 7 a 12 los sábados`
    },
    {
      label: 'FORMAS DE PAGO',
      text: `Disponemos de las siguientes formas de pago para su comodidad:

•  Transferencia / Depósito:
Banco Familiar
Cta.Cte.N.º 45 - 1115238
DOMOSMART S.A.
    Ruc 80100254 - 0

Alias ruc
80100254 - 0

•  Débito Automático - con su tarjeta de crédito.

•  Giro TIGO al número 0981 - 218124 o al 0984 - 839232(Puede realizar carga de billetera)

Precisamos envíe el comprobante al 0981218124 una vez realice el pago, por favor`
    },
    {
      label: 'FORMA DE PAGO - ALIAS RUC',
      text: `Alias Ruc:
80100254 - 0`
    },
    {
      label: 'FORMA DE PAGO - TRANSFERENCIA/DEPÓSITO',
      text: `•  Transferencia / Depósito en cuenta:
Banco Familiar
Cta.Cte.N.º 45 - 1115238
DOMOSMART S.A.
    Ruc 80100254 - 0

Alias Ruc:
80100254 - 0

Precisamos envíe el comprobante al 0981218124 una vez realice el pago por favor`
    },
    {
      label: 'FORMA DE PAGO - GIRO TIGO',
      text: `•  Giro TIGO al número 0981 - 218124 o al 0984 - 839232(puede realizar carga de billetera)

Precisamos nos pueda enviar el comprobante al 0981218124 una vez realice el pago por favor`
    },
    {
      label: 'INFO CICLO FACTURACIÓN',
      text: `Le explico lo que sería el ciclo de facturación que tiene como política la empresa:
Nuestro ciclo es mes adelantado.Por eso que al finalizar la instalación, usted ya esta abonando la 1ra cuota.
Las facturas se emiten el 1ro de cada mes y vencen el día 10.
Si para la fecha 17 no se registra un pago; el sistema procede a cortes en el servicio. (reactivar el servicio tiene un coste de 10.000gs, por cada contrato)`
    },
    {
      label: 'INFO PROMO REFERIDO',
      text: `Le comento que contamos con una Promo de Referidos:

Por ejemplo, si usted refiere a una persona nuestros servicios y esa persona instala, usted recibe un descuento del 50 % en la siguiente cuota de un servicio.`
    },
    {
      label: 'ENLACES ACCESO SISTEMA',
      text: `Le facilito los enlaces para que pueda acceder al sistema, desde su celular, pc o Tablet, a través de cualquier navegador, por medio de la web:

https://www.domo.com.py/app#/login

Le facilito el link de descarga para Android, en Google Play:
https://play.google.com/store/apps/details?id=com.domo.app

y el link de descarga para IOS, en App Store:
https://apps.apple.com/py/app/domosmart/id1330205574`
    },
    {
      label: 'CONTACTO COBRANZAS',
      text: `Le facilito el contacto del area de cobranzas para cualquier consulta con referente a pagos 👇🏻

wa.me/595981218124`
    },
    {
      label: 'INFO ASISTENCIA',
      text: `Le comento que las asistencias son sin costo en caso de que el inconveniente sea propio del equipo o de la instalacion.
En caso contrario, si el inconveniente es generado por un factor externo al equipo o la instalación, tendría un coste de 150.000 gs, para que pueda tener en cuenta`
    },
    {
      label: 'INFO UBICACIÓN/DISPONIBILIDAD TÉCNICO',
      text: `Si me puede facilitar la ubicación de donde desea que se realice el trabajo por favor y así verifico la disponibilidad de un técnico en la zona`
    }
  ];

  const classes = useStyles();
  const companyHeadquarterId = localStorage.selectedUserHeadquarter;
  const isPollingRef = useRef(false);
  const pendingMediaRef = useRef(0);
  const [list, setList] = useState({ rows: [], count: 0 });
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const [selectedConversation, setSelectedConversation] = useState({});
  const [conversation, setConversation] = useState({ rows: [], count: 0 });
  const [showConversationDialog, setShowConversationDialog] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [orderBy, setOrderBy] = useState({ columnName: null, orderBy: null });
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [conversationToClose, setConversationToClose] = useState(null);

  const handleMediaStatusChange = React.useCallback((status) => {
    if (status === 'START') {
      pendingMediaRef.current += 1;
    } else if (status === 'END') {
      pendingMediaRef.current = Math.max(0, pendingMediaRef.current - 1);
    }
  }, []);

  async function fetchList() {
    const response = await getConversationList({
      companyHeadquarterId,
      search,
      orderBy: orderBy.columnName
        ? `${orderBy.columnName} ${orderBy.orderBy}`
        : '',
      page,
      limit
    });
    const normalizedResponse = {
      rows: response.rows.map((row) => ({
        ...row,
        text: row.text?.length > 50 ? `${row.text.slice(0, 50)}...` : row.text
      })),
      count: response.count
    };
    setList(normalizedResponse);
  }

  async function fetchConversation() {
    if (!selectedConversation.fromName) return;
    if (pendingMediaRef.current > 0 || isPollingRef.current) return;

    try {
      isPollingRef.current = true;
      const response = await getConversationListFromName({
        companyHeadquarterId,
        search,
        orderBy: orderBy.columnName
          ? `${orderBy.columnName} ${orderBy.orderBy}`
          : '',
        page,
        limit: 1000000,
        selectedConversationFromName: selectedConversation.fromName
      });
      setConversation((prev) => {
        if (prev.rows.length === response.rows.length) {
          const lastPrev = prev.rows[prev.rows.length - 1];
          const lastNew = response.rows[response.rows.length - 1];
          if (lastPrev && lastNew && lastPrev.id === lastNew.id) return prev;
        }
        return response;
      });
    } catch (error) {
      console.error('Error en polling', error);
    } finally {
      isPollingRef.current = false;
    }
  }

  useEffect(() => {
    fetchList();
  }, [page, limit, search, orderBy]);

  useEffect(() => {
    if (!showConversationDialog || !selectedConversation?.fromName) return;
    fetchConversation();
  }, [showConversationDialog, selectedConversation?.fromName]);

  useEffect(() => {
    const handleSseNotification = (event) => {
      if (showConversationDialog && selectedConversation?.fromName) {
        const sseData = event.detail;
        const message = sseData?.payload;

        // Comprobar si el mensaje pertenece a la conversación abierta
        // En INBOUND el remitente es 'from', en OUTBOUND el destinatario es 'to'
        if (
          message &&
          (message.from === selectedConversation.fromName ||
            message.to === selectedConversation.fromName)
        ) {
          event.preventDefault();
          fetchConversation();
        }
      } else if (!showConversationDialog && !openConfirmDialog) {
        fetchList();
      }
    };

    window.addEventListener('sse-notification', handleSseNotification);

    return () => {
      window.removeEventListener('sse-notification', handleSseNotification);
    };
  }, [
    showConversationDialog,
    openConfirmDialog,
    selectedConversation,
    search,
    page,
    limit,
    orderBy
  ]);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = scrollContainerRef.current;
      isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 50;
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    isAtBottomRef.current = true;
  };

  const handleSearch = (searchValue) => {
    setPage(0);
    setSearch(searchValue.target.value);
  };
  const handleChangePage = (pageIndex) => {
    setPage(pageIndex);
  };
  const handleChangeLimit = (limitIndex) => {
    setLimit(limitIndex);
  };
  const handleOrderBy = (column) => {
    setOrderBy(column);
  };

  const handleSendMessage = async (textToSend) => {
    try {
      await sendMessage({
        messageId: uuid(),
        to: selectedConversation.fromName,
        text: textToSend,
        channel: 'WHATSAPP',
        metadata: {
          customerId: selectedConversation.metadata?.customerId,
          customerCode: selectedConversation.metadata?.customerCode,
          customerBusinessName:
            selectedConversation.metadata?.customerBusinessName,
          contactFullName: selectedConversation.metadata?.contactFullName,
          fullName: `${user.firstname} ${user.surnames}`
        },
        userId: user.id
      });
      await fetchConversation();
      scrollToBottom();
    } catch (error) {
      console.error('Error enviando:', error);
      throw error;
    }
  };

  const handleConversationDialogClose = async () => {
    setConversation({ rows: [], count: 0 });
    setSelectedConversation({});
    pendingMediaRef.current = 0;
    await fetchList();
    setShowConversationDialog(false);
  };

  const handleConversation = async (event, id, row) => {
    setSelectedConversation(row);
    setShowConversationDialog(true);
    isAtBottomRef.current = true;
    await fetchConversation();
  };

  const handleClickClose = (event, id, row) => {
    setConversationToClose(row);
    setOpenConfirmDialog(true);
  };

  const handleConfirmClose = async (comments) => {
    try {
      const finalComment = comments ? comments.trim() : '';
      const finalText = finalComment
        ? `CONVERSACIÓN CERRADA\n${finalComment}`
        : 'CONVERSACIÓN CERRADA';

      await sendMessage({
        messageId: uuid(),
        to: conversationToClose.fromName,
        text: finalText,
        type: 'CLOSED',
        channel: 'WHATSAPP',
        metadata: {
          customerId: conversationToClose.metadata?.customerId,
          customerCode: conversationToClose.metadata?.customerCode,
          customerBusinessName:
            conversationToClose.metadata?.customerBusinessName,
          contactFullName: conversationToClose.metadata?.contactFullName,
          fullName: `${user.firstname} ${user.surnames}`
        },
        userId: user.id
      });
      setOpenConfirmDialog(false);
      setConversationToClose(null);
      await fetchList();
    } catch (error) {
      console.error('Error al cerrar conversación:', error);
      alert('Error al cerrar conversación, inténtelo de nuevo más tarde');
    }
  };

  const handleMediaLoaded = () => {
    if (isAtBottomRef.current && messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
      }, 1000);
    }
  };

  const columnNameList = [
    'createdAt',
    'origin',
    'metadata.contactFullName',
    'metadata.customerBusinessName',
    'metadata.customerCode',
    'fromName',
    'text'
  ];

  return (
    <Page className={classes.root} title="Conversaciones">
      <Dialog
        fullScreen
        open={showConversationDialog}
        onClose={handleConversationDialogClose}
        scroll="paper"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle className={classes.header}>
          <IconButton onClick={handleConversationDialogClose}>
            <X size={24} />
          </IconButton>
          {selectedConversation?.metadata?.contactFullName}{' '}
          {selectedConversation?.fromName}
        </DialogTitle>

        <MessagesList
          rows={conversation.rows}
          classes={classes}
          scrollRef={scrollContainerRef}
          messagesEndRef={messagesEndRef}
          onScroll={handleScroll}
          onMediaLoaded={handleMediaLoaded}
          onStatusChange={handleMediaStatusChange}
        />

        <MessageInput
          onSendMessage={handleSendMessage}
          templates={TEMPLATES}
          user={user}
        />
      </Dialog>

      <ClosureDialog
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
          setConversationToClose(null);
        }}
        onConfirm={handleConfirmClose}
        conversationToClose={conversationToClose}
      />

      <Container maxWidth={false}>
        <IconButton
          color="primary"
          onClick={fetchList}
          className={classes.refreshButton}
        >
          <RefreshCw size={20} />
        </IconButton>
        <Datatable
          domainName="CONVERSATION"
          title="Conversaciones"
          dataList={list.rows}
          count={list.count}
          onSearch={handleSearch}
          handleClickEdit={handleConversation}
          handleClickClose={handleClickClose}
          onChangePageValue={handleChangePage}
          onChangePageLimit={handleChangeLimit}
          handleOrderBy={handleOrderBy}
          rowsPerPage={1000}
          columnNameList={columnNameList}
          order={Boolean(false)}
          pagination={Boolean(false)}
        />
      </Container>
    </Page>
  );
};

export default ConversationListView;
